import React, { useState, useRef, useEffect } from 'react';
import Hls from 'hls.js';
import { AudioTrack } from '../types';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../hooks/useLocale';
import { getLocalizedValue } from '../utils/i18n';

const VOLUME_STORAGE_KEY = 'audioPlayer:volume';
const MUTED_STORAGE_KEY = 'audioPlayer:muted';

const readStoredVolume = (): number => {
  try {
    const raw = localStorage.getItem(VOLUME_STORAGE_KEY);
    if (raw === null) return 1;
    const parsed = parseFloat(raw);
    if (Number.isFinite(parsed)) return Math.min(1, Math.max(0, parsed));
  } catch (_) {}
  return 1;
};

const readStoredMuted = (): boolean => {
  try {
    return localStorage.getItem(MUTED_STORAGE_KEY) === '1';
  } catch (_) {
    return false;
  }
};

interface AudioPlayerProps {
  track: AudioTrack;
  audioUrl?: string | null;
  playlistContent?: string; // HLS playlist content
  streamingType?: 'url' | 'hls'; // Streaming type
  sessionDuration?: number; // Session duration in seconds
  onTrackEnd?: () => void;
  onSessionEnd?: () => void; // Called when session duration is reached
  onTimeUpdate?: (
    currentTime: number,
    duration: number,
    isPlaying: boolean,
  ) => void;
  showCountdown?: boolean; // Mobile mode: full-screen player (no countdown)
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  track,
  playlistContent,
  streamingType = 'url',
  sessionDuration,
  onTrackEnd,
  onSessionEnd,
  onTimeUpdate,
  showCountdown = false,
}) => {
  const { t } = useTranslation('common');
  const { t: tTracks } = useTranslation('tracks');
  const audioRef = useRef<HTMLAudioElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const hlsPlaylistUrlRef = useRef<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const isPlayingRef = useRef(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(() => readStoredVolume());
  const [isMuted, setIsMuted] = useState(() => readStoredMuted());
  const [isLoading, setIsLoading] = useState(false);
  const [sessionTime, setSessionTime] = useState(0); // Track total session time
  const sessionTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Keep refs in sync so event handlers can read them without stale closures
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  // Persist volume + mute state and apply to the audio element
  useEffect(() => {
    try {
      localStorage.setItem(VOLUME_STORAGE_KEY, String(volume));
    } catch (_) {}
    const audio = audioRef.current;
    if (audio && !isMuted) audio.volume = volume;
  }, [volume, isMuted]);

  useEffect(() => {
    try {
      localStorage.setItem(MUTED_STORAGE_KEY, isMuted ? '1' : '0');
    } catch (_) {}
    const audio = audioRef.current;
    if (audio) audio.volume = isMuted ? 0 : volume;
  }, [isMuted, volume]);

  const { currentLocale } = useLocale();

  // CDN URL from environment variable
  const cdnUrl = process.env.REACT_APP_CDN_URL || '';

  // Get full thumbnail URL with CDN prefix
  const getThumbnailUrl = (thumbnail?: string): string | undefined => {
    if (!thumbnail) return undefined;
    if (thumbnail.startsWith('http://') || thumbnail.startsWith('https://')) {
      return thumbnail;
    }
    const separator = thumbnail.startsWith('/') ? '' : '/';
    return `${cdnUrl}${separator}${thumbnail}`;
  };

  const thumbnailUrl = getThumbnailUrl(track.thumbnail);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => {
      setCurrentTime(audio.currentTime);
      onTimeUpdate?.(audio.currentTime, audio.duration || 0, !audio.paused);
      if (
        'mediaSession' in navigator &&
        audio.duration &&
        !isNaN(audio.duration) &&
        isFinite(audio.duration)
      ) {
        try {
          navigator.mediaSession.setPositionState({
            duration: audio.duration,
            playbackRate: audio.playbackRate,
            position: audio.currentTime,
          });
        } catch (_) {}
      }
    };
    const updateDuration = () => {
      setDuration(audio.duration);
      onTimeUpdate?.(audio.currentTime || 0, audio.duration, !audio.paused);
    };
    const handleEnded = () => {
      setIsPlaying(false);
      onTrackEnd?.();
    };
    const handleLoadStart = () => setIsLoading(true);

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadstart', handleLoadStart);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadstart', handleLoadStart);
    };
  }, [onTrackEnd]);

  // Session timer effect
  useEffect(() => {
    if (isPlaying && sessionDuration) {
      sessionTimerRef.current = setInterval(() => {
        setSessionTime((prevTime) => {
          const newTime = prevTime + 1;

          if (newTime >= sessionDuration) {
            const audio = audioRef.current;
            if (audio) {
              audio.pause();
              setIsPlaying(false);
            }
            onSessionEnd?.();
            return sessionDuration;
          }

          return newTime;
        });
      }, 1000);
    } else {
      if (sessionTimerRef.current) {
        clearInterval(sessionTimerRef.current);
        sessionTimerRef.current = null;
      }
    }

    return () => {
      if (sessionTimerRef.current) {
        clearInterval(sessionTimerRef.current);
        sessionTimerRef.current = null;
      }
    };
  }, [isPlaying, sessionDuration, onSessionEnd]);

  // Reset session time when sessionDuration changes
  useEffect(() => {
    setSessionTime(0);
  }, [sessionDuration]);

  // ── Media Session API ─────────────────────────────────────────────────────

  // Set track metadata (title, artwork) for OS lock screen / notification widget
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    const title = getLocalizedValue(track.name, currentLocale);
    const artwork: MediaImage[] = thumbnailUrl
      ? [{ src: thumbnailUrl, sizes: '512x512', type: 'image/jpeg' }]
      : [];
    navigator.mediaSession.metadata = new MediaMetadata({
      title,
      artist: 'Presencia App',
      artwork,
    });
  }, [track, currentLocale, thumbnailUrl]);

  // Sync OS playback state indicator
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
  }, [isPlaying]);

  // Register OS media control action handlers
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    navigator.mediaSession.setActionHandler('play', () => {
      audioRef.current
        ?.play()
        .then(() => setIsPlaying(true))
        .catch(() => {});
    });
    navigator.mediaSession.setActionHandler('pause', () => {
      audioRef.current?.pause();
      setIsPlaying(false);
    });
    navigator.mediaSession.setActionHandler('seekforward', (details) => {
      const audio = audioRef.current;
      if (!audio) return;
      const skip = details.seekOffset ?? 10;
      audio.currentTime = Math.min(
        audio.currentTime + skip,
        audio.duration || Infinity,
      );
    });
    navigator.mediaSession.setActionHandler('seekbackward', (details) => {
      const audio = audioRef.current;
      if (!audio) return;
      const skip = details.seekOffset ?? 10;
      audio.currentTime = Math.max(audio.currentTime - skip, 0);
    });
    return () => {
      (
        ['play', 'pause', 'seekforward', 'seekbackward'] as MediaSessionAction[]
      ).forEach((action) =>
        navigator.mediaSession.setActionHandler(action, null),
      );
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Audio/HLS loading effect
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    setIsLoading(true);

    if (streamingType === 'hls' && playlistContent) {
      console.log('Initializing HLS player');

      if (Hls.isSupported()) {
        const hls = new Hls({
          debug: false,
          enableWorker: true,
          lowLatencyMode: false,
          maxBufferLength: 30,
          maxMaxBufferLength: 60,
          maxBufferSize: 60 * 1000 * 1000,
          maxBufferHole: 0.5,
          backBufferLength: 90,
          xhrSetup: (xhr) => {
            xhr.withCredentials = true;
          },
        });

        const playlistBlob = new Blob([playlistContent], {
          type: 'application/vnd.apple.mpegurl',
        });
        const playlistUrl = URL.createObjectURL(playlistBlob);
        hlsPlaylistUrlRef.current = playlistUrl;

        hls.loadSource(playlistUrl);
        hls.attachMedia(audio);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          console.log('HLS manifest parsed, ready to play');
          setIsLoading(false);
        });

        hls.on(Hls.Events.ERROR, (event, data) => {
          console.error('HLS error:', data);
          if (data.fatal) {
            // If the audio element has already ended naturally, ignore errors —
            // hls.js can raise network errors after the last segment has played
            // because it tries to fetch a segment that doesn't exist.
            if (audioRef.current?.ended) return;

            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.error('Network error, attempting recovery');
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.error('Media error, attempting recovery');
                hls.recoverMediaError();
                break;
              default:
                console.error('Fatal error, cannot recover');
                setIsLoading(false);
                hls.destroy();
                break;
            }
          }
        });

        hlsRef.current = hls;

        return () => {
          hlsPlaylistUrlRef.current = null;
          URL.revokeObjectURL(playlistUrl);
          hls.destroy();
        };
      } else if (audio.canPlayType('application/vnd.apple.mpegurl')) {
        console.log('Using native HLS support');
        const playlistBlob = new Blob([playlistContent], {
          type: 'application/vnd.apple.mpegurl',
        });
        const playlistUrl = URL.createObjectURL(playlistBlob);
        audio.src = playlistUrl;

        return () => {
          URL.revokeObjectURL(playlistUrl);
        };
      } else {
        console.error('HLS not supported in this browser');
        setIsLoading(false);
      }
    } else {
      console.log('Using standard audio streaming');
      const sourceUrl = track.url;
      audio.src = sourceUrl;
      audio.preload = 'metadata';
    }
  }, [track, playlistContent, streamingType]);

  // Autoplay effect — depends only on track.id so it never re-runs on
  // play/pause toggles. isPlayingRef lets the handler read the latest value
  // without creating a stale closure. The audio.ended guard prevents the
  // handler from restarting the track after it has naturally finished.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleCanPlay = () => {
      if (audio && !isPlayingRef.current && !audio.ended) {
        audio
          .play()
          .then(() => {
            setIsPlaying(true);
            setIsLoading(false);
          })
          .catch((error) => {
            console.warn('Autoplay failed:', error);
            setIsLoading(false);
          });
      }
    };

    audio.addEventListener('canplay', handleCanPlay);

    return () => {
      audio.removeEventListener('canplay', handleCanPlay);
    };
  }, [track.id]); // intentionally excludes isPlaying — use isPlayingRef instead

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      onTimeUpdate?.(sessionTime, audio.duration || 0, false);
    } else {
      audio.play();
      setIsPlaying(true);
      onTimeUpdate?.(sessionTime, audio.duration || 0, true);
    }
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isMuted) {
      audio.volume = volume;
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  };

  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleProgressBarSeek = (newTime: number) => {
    const audio = audioRef.current;
    const hls = hlsRef.current;

    if (!audio) return;

    const currentTime = audio.currentTime;
    const seekDelta = newTime - currentTime;
    const FORWARD_SEEK_THRESHOLD = 20;

    if (hls && streamingType === 'hls' && seekDelta > FORWARD_SEEK_THRESHOLD) {
      hls.stopLoad();
      audio.currentTime = newTime;
      setCurrentTime(newTime);
      hls.startLoad(newTime);
    } else {
      audio.currentTime = newTime;
      setCurrentTime(newTime);
    }
    setSessionTime(newTime);
  };

  const VolumeIcon = () =>
    isMuted || volume === 0 ? (
      <svg className='w-5 h-5' fill='white' viewBox='0 0 20 20'>
        <path
          fillRule='evenodd'
          d='M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.816L4.383 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.383l4-3.816a1 1 0 011-.108zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z'
          clipRule='evenodd'
        />
      </svg>
    ) : (
      <svg className='w-5 h-5' fill='white' viewBox='0 0 20 20'>
        <path
          fillRule='evenodd'
          d='M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.816L4.383 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.383l4-3.816a1 1 0 011-.108zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828a1 1 0 010-1.415z'
          clipRule='evenodd'
        />
      </svg>
    );

  // ─── MOBILE: Full-screen Spotify-style player ──────────────────────────────
  if (showCountdown) {
    const sessionRemaining = sessionDuration
      ? sessionDuration - sessionTime
      : 0;

    return (
      <div className='audio-player audio-player--mobile'>
        <audio ref={audioRef} preload='metadata' />

        {/* Blurred background */}
        {thumbnailUrl && (
          <div
            className='mobile-player-bg'
            style={{ backgroundImage: `url(${thumbnailUrl})` }}
          />
        )}

        <div className='mobile-player-inner'>
          {/* Album Art */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              alignItems: 'center',
            }}
          >
            <div className='mobile-player-art-wrap'>
              {thumbnailUrl ? (
                <img
                  src={thumbnailUrl}
                  alt={getLocalizedValue(track.name, currentLocale)}
                  className='mobile-player-art'
                />
              ) : (
                <div className='mobile-player-art mobile-player-art--placeholder'>
                  <svg
                    width='72'
                    height='72'
                    viewBox='0 0 24 24'
                    fill='currentColor'
                  >
                    <path d='M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z' />
                  </svg>
                </div>
              )}
              {/* Play / Pause */}
              <div className='mobile-player-controls'>
                {isLoading ? (
                  <div className='mobile-player-spinner'>
                    <div className='spinner glow' />
                  </div>
                ) : sessionDuration ? (
                  <button
                    className='mobile-player-play-btn'
                    onClick={togglePlayPause}
                  >
                    {isPlaying ? (
                      <svg viewBox='0 0 24 24' fill='white'>
                        <path d='M6 19h4V5H6v14zm8-14v14h4V5h-4z' />
                      </svg>
                    ) : (
                      <svg viewBox='0 0 24 24' fill='white'>
                        <path d='M8 5v14l11-7z' />
                      </svg>
                    )}
                  </button>
                ) : null}
              </div>
            </div>
            {/* Track info */}
            <div className='mobile-player-info'>
              <div className='mobile-player-title'>
                {getLocalizedValue(track.name, currentLocale)}
              </div>
              <div className='mobile-player-category'>
                {(track.tags || []).map((tag) => (
                  <span key={tag} className='track-tag-chip'>
                    {tTracks(`tags.${tag}`, tag)}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div className='mobile-player-progress-section'>
            <div
              className='mobile-player-progress-track'
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                handleProgressBarSeek(
                  ((e.clientX - rect.left) / rect.width) * duration,
                );
              }}
            >
              <div
                className='mobile-player-progress-fill'
                style={{
                  width: `${duration ? (currentTime / duration) * 100 : 0}%`,
                }}
              />
            </div>
            {/*
              <div className='mobile-player-times'>
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
              </div>
              */}
            {/* Session badge */}
            {sessionDuration && (
              <div className='mobile-player-session'>
                <svg
                  width='13'
                  height='13'
                  viewBox='0 0 24 24'
                  fill='currentColor'
                >
                  <path d='M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z' />
                </svg>
                <span>{formatTime(sessionRemaining)} restante</span>
              </div>
            )}
          </div>

          {/* Volume */}
          <div className='mobile-player-volume'>
            <button onClick={toggleMute} className='volumen-button'>
              <VolumeIcon />
            </button>
            <div
              className='volume-slider'
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const newVolume = Math.max(
                  0,
                  Math.min(1, (e.clientX - rect.left) / rect.width),
                );
                if (audioRef.current) {
                  audioRef.current.volume = newVolume;
                  setVolume(newVolume);
                  setIsMuted(false);
                }
              }}
            >
              <div
                className='volume-slider-bar'
                style={{ width: `${(isMuted ? 0 : volume) * 100}%` }}
              />
            </div>
          </div>

          {/* Headphones reminder */}
          <div className='player-headphones-hint'>
            <svg
              width='16'
              height='16'
              viewBox='0 0 24 24'
              fill='currentColor'
              style={{
                flexShrink: 0,
                border: '1px solid #bfbfbf',
                padding: '5px',
                width: '30px',
                height: '30px',
                borderRadius: '50%',
              }}
            >
              <path d='M12 1C7.03 1 3 5.03 3 10v6c0 1.66 1.34 3 3 3h1V10H5c0-3.87 3.13-7 7-7s7 3.13 7 7h-2v9h1c1.66 0 3-1.34 3-3v-6c0-4.97-4.03-9-9-9z' />
            </svg>
            <span>{t('player.headphonesReminder')}</span>
          </div>
        </div>
      </div>
    );
  }

  // ─── DESKTOP: Compact 3-column bottom bar ──────────────────────────────────
  return (
    <div className='audio-player audio-player--desktop'>
      <audio ref={audioRef} preload='metadata' />

      <div className='desktop-player-inner'>
        {/* Left: track info */}
        <div className='track-info'>
          {thumbnailUrl && (
            <div className='track-thumbnail-wrapper'>
              <img
                src={thumbnailUrl}
                alt={getLocalizedValue(track.name, currentLocale)}
                className='track-thumbnail'
              />
            </div>
          )}
          <div className='track-specs'>
            <div className='track-title'>
              {getLocalizedValue(track.name, currentLocale)}
            </div>
            <div className='track-category'>
              {(track.tags || []).map((tag) => (
                <span key={tag} className='track-tag-chip'>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Center: play + seekbar */}
        <div className='desktop-player-center'>
          <div className='player-seekbar'>
            <div className='play-pause-button-wrapper'>
              {isLoading ? (
                <div className='spinner' />
              ) : sessionDuration ? (
                <button
                  className='play-pause-button'
                  onClick={togglePlayPause}
                  disabled={isLoading}
                >
                  {isPlaying ? (
                    <svg fill='white' viewBox='0 0 20 20'>
                      <path
                        fillRule='evenodd'
                        d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z'
                        clipRule='evenodd'
                      />
                    </svg>
                  ) : (
                    <svg fill='white' viewBox='0 0 20 20'>
                      <path
                        fillRule='evenodd'
                        d='M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z'
                        clipRule='evenodd'
                      />
                    </svg>
                  )}
                </button>
              ) : null}
            </div>
            <span className='audio-time'>{formatTime(currentTime)}</span>
            <div
              className='audio-progress'
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const percentage = (e.clientX - rect.left) / rect.width;
                handleProgressBarSeek(percentage * duration);
              }}
            >
              <div
                className='audio-progress-bar'
                style={{
                  width: `${duration ? (currentTime / duration) * 100 : 0}%`,
                }}
              />
            </div>
            <span className='audio-time'>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Right: volume */}
        <div className='audio-volume'>
          <button onClick={toggleMute} className='volumen-button'>
            <VolumeIcon />
          </button>
          <div
            className='volume-slider'
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const newVolume = Math.max(
                0,
                Math.min(1, (e.clientX - rect.left) / rect.width),
              );
              if (audioRef.current) {
                audioRef.current.volume = newVolume;
                setVolume(newVolume);
                setIsMuted(false);
              }
            }}
          >
            <div
              className='volume-slider-bar'
              style={{ width: `${(isMuted ? 0 : volume) * 100}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
